// @givmo/employer-module — the pickers' read (EP-SUITE8): `GET /admin/company/module-choices`, every
// app the company could give, each `{module, on, roles}`, decoded tolerantly (`app-choice.ts`).
//
// Global Admin only on the server (`company_admin:edit`), which is exactly who reaches the two acts
// that read it — approving a join request and changing an admin's role — and it serves on the
// portal's host whatever the console's flags say (OM-57). One page is the whole list: the catalog
// is eight apps, the page a hundred.

import { useQuery } from '@tanstack/react-query';
import { sdk } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { decodeAppChoices } from './app-choice';
import type { AppListStatus } from './app-choice-fields';

export const APP_CHOICES_KEY = ['companies-list_company_module_choices'] as const;

/** The list for a picker that is showing: re-read on every open (`staleTime: 0`). */
export function useAppChoices(enabled: boolean) {
  const query = useQuery({
    queryKey: APP_CHOICES_KEY,
    enabled,
    queryFn: async ({ signal }) => {
      const { data } = await sdk.companiesListCompanyModuleChoices({
        query: { 'page[limit]': 100, 'page[offset]': 0 },
        signal,
        throwOnError: true,
      });
      return decodeAppChoices(extractList<unknown>(data));
    },
    staleTime: 0,
  });
  const rows = query.data ?? null;
  // A list once read stays on screen through a failed re-read; only no list at all is an error.
  const status: AppListStatus = rows !== null ? 'ready' : query.isError ? 'error' : 'loading';
  return { rows, status, refetch: () => void query.refetch() };
}
